import { Snackbar } from '@mui/material';
import createBroadcastChannel, {
  BroadcastChannels,
} from '@platform-ui-common/utilities/broadcastChannel.js';
import uniqueId from 'lodash/uniqueId.js';
import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

export type SnackbarKey = string;
type SnackbarAction = ReactNode | ((key: SnackbarKey) => ReactNode);
export interface SnackbarState {
  showSnackbar: (message: ReactNode, action?: SnackbarAction) => SnackbarKey;
  removeSnackbar: (id: SnackbarKey) => void;
}

const SnackbarContext = createContext<SnackbarState>({} as SnackbarState);

interface Snack {
  key: SnackbarKey;
  message: ReactNode;
  action?: SnackbarAction;
}

export function SnackbarProvider({ children }: { children: ReactNode }): JSX.Element {
  const [snacks, setSnacks] = useState<readonly Snack[]>([]);

  const showSnackbar = useCallback((message: ReactNode, action?: SnackbarAction): SnackbarKey => {
    const key = uniqueId();
    setSnacks((prev) => [...prev, { message, action, key }]);
    return key;
  }, []);

  const removeSnackbar = useCallback(
    (keyToClose: SnackbarKey) => setSnacks((prev) => prev.filter(({ key }) => key !== keyToClose)),
    [],
  );

  const handleClose = (): void => setSnacks((prev) => prev.slice(1));

  useEffect(() => {
    const notificationChannel = createBroadcastChannel(BroadcastChannels.Notification);
    notificationChannel.onmessage = (event) => {
      if (event.isTrusted && !!event.data) showSnackbar(event.data);
    };
    return () => notificationChannel.close();
  });

  const activeSnack = snacks[0];
  const action =
    typeof activeSnack?.action === 'function'
      ? activeSnack.action(activeSnack.key)
      : activeSnack?.action;

  return (
    <SnackbarContext.Provider value={{ showSnackbar, removeSnackbar }}>
      <Snackbar
        key={activeSnack?.key}
        data-testid="snackbar"
        open={!!activeSnack}
        autoHideDuration={7000}
        onClose={handleClose}
        message={activeSnack?.message}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      {children}
    </SnackbarContext.Provider>
  );
}

export default function useSnackbar(): SnackbarState {
  return useContext(SnackbarContext);
}
